import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const START_EVENT = "matematron:navigation-start";
const PENDING_CLASS = "is-action-pending";
const FEEDBACK_DELAY_MS = 180;
const FALLBACK_CLEAR_MS = 1400;

function getButtonTrigger(candidate) {
  if (typeof HTMLElement === "undefined") return null;
  return candidate instanceof HTMLElement && candidate.matches("button, a")
    ? candidate
    : null;
}

export function announceNavigationStart(trigger = null, label = "Przechodzę…") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(START_EVENT, {
    detail: {
      trigger: getButtonTrigger(trigger),
      label,
    },
  }));
}

export default function GlobalNavigationFeedback() {
  const location = useLocation();
  const [feedback, setFeedback] = useState(null);
  const activeRef = useRef(null);
  const showTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);

  const clearFeedback = useCallback(() => {
    if (showTimerRef.current) window.clearTimeout(showTimerRef.current);
    if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
    showTimerRef.current = null;
    fallbackTimerRef.current = null;

    const trigger = activeRef.current?.trigger;
    if (trigger) {
      trigger.classList.remove(PENDING_CLASS);
      trigger.removeAttribute("aria-busy");
    }
    activeRef.current = null;
    setFeedback(null);
  }, []);

  useEffect(() => {
    const onStart = (event) => {
      clearFeedback();
      const trigger = getButtonTrigger(event.detail?.trigger);
      const label = event.detail?.label || "Przechodzę…";

      if (trigger) {
        trigger.classList.add(PENDING_CLASS);
        trigger.setAttribute("aria-busy", "true");
      }
      activeRef.current = { trigger, label };
      showTimerRef.current = window.setTimeout(() => {
        if (activeRef.current?.label === label) setFeedback({ label });
      }, FEEDBACK_DELAY_MS);
      fallbackTimerRef.current = window.setTimeout(clearFeedback, FALLBACK_CLEAR_MS);
    };

    window.addEventListener(START_EVENT, onStart);
    return () => {
      window.removeEventListener(START_EVENT, onStart);
      clearFeedback();
    };
  }, [clearFeedback]);

  useEffect(() => {
    if (activeRef.current) clearFeedback();
  }, [clearFeedback, location.pathname, location.search, location.hash]);

  return feedback ? (
    <div className="global-navigation-feedback" role="status" aria-live="polite">
      <span className="global-navigation-feedback__spinner" aria-hidden="true" />
      {feedback.label}
    </div>
  ) : null;
}
